import { Injectable } from "@angular/core";
import { Http } from "@angular/http";
import {
    SERVICE_URLS,
    ITCIWebSocketService,
    ITCIMetrics,
    TCIMetricsLabelType,
    ITCIMetricsStatus,
    ITCIApplication,
    TCIMetricsAppStatusType,
    TCIMetricsExecutionsStatusType,
    ITCIHybridMetrics,
    TCITenantAppType,
    TCIAppStatusCategory,
    mapIServiceToTenantAppType
} from "@tci/common";
import { NgWebSocketService } from "../../ng-shared/ng-services/ng-websocket.service";
import { NgEnvVarsService } from "../../ng-shared/ng-services/ng-env-vars.service";
import { TCITenantRemoteServices } from "../../ng-tenant/tci-tenant-remote-services";

import { Observable, Subject } from "rxjs";
import * as lodash from "lodash";

export interface IMetricsStatusSummaryData {
    name: string;
    type: TCIAppStatusCategory;
    value: number;
    percentage?: number;
}

export interface IMetricsStatusSummary {
    totalApps: IMetricsStatusSummaryData;
    goodNum: IMetricsStatusSummaryData;
    notRunningNum: IMetricsStatusSummaryData;
    warningNum: IMetricsStatusSummaryData;
    errorNum: IMetricsStatusSummaryData;
}

export interface IMetricsExecutionsSummary {
    tenantType: TCITenantAppType;
    appCount: number;
    instancesCount: number;
    successCount: number;
    failedCount: number;
}

export interface IMetricsTopExecutions {
    app: ITCIApplication;
    count: number;
}

export interface IMetricsRefresh {
    refresh: boolean;
}

@Injectable()
export class NgAppListHeaderService {
    private apps: ITCIApplication[] = [];
    private metricsHttpObs$: Observable<ITCIMetrics>;

    private summaryData$: Subject<IMetricsStatusSummary> = new Subject();
    private totalExecutionsData$: Subject<IMetricsExecutionsSummary[]> = new Subject();
    private topExecutionsData$: Subject<IMetricsTopExecutions[]> = new Subject();
    private statusFilter$: Subject<IMetricsStatusSummaryData> = new Subject();
    private dashboardRefreshSub$: Subject<IMetricsRefresh> = new Subject();
    /**
     * constructor
     * @param envVarsService
     * @param websocketService
     */
    constructor(
        private http: Http,
        private tenantRemoteServices: TCITenantRemoteServices,
        private envVarsService: NgEnvVarsService,
        private websocketService: NgWebSocketService
    ) {
        this.metricsHttpObs$ = this.http.get(SERVICE_URLS.METRICS_RECORDS)
            .map(response => response.json())
            .share();
    }


    setAppsCollection(apps: ITCIApplication[]) {
        this.apps = apps;
        this.setDashboardRefSub({refresh: true});
        this.refresh();
    }

    refresh() {
        const tenantsObs = this.tenantRemoteServices.getRemoteServices()
            .flatMap(svcs => svcs)
            .map(svc => mapIServiceToTenantAppType(svc))
            .distinct(svc => svc.toString())
            .toArray();
        // UI services do not map to tenant app type
        // .map(uiServices => uiServices.map(srv => TCITenantAppType.fromString(srv.name)));

        Observable.combineLatest(this.getMetrics(), tenantsObs, (metrics, tenantTypes) => ({
            metrics,
            tenantTypes: tenantTypes
        }))
            .subscribe(res => {
                const filter = (data: ITCIMetricsStatus) => TCITenantAppType.fromString(data.labels.app_type).appliesTo(res.tenantTypes);

                const summaryData = (res.metrics.labeledOrgMetrics[TCIMetricsLabelType.tci_apps] || [])
                    .filter(data => filter(data));
                this.genStateSummaryData(summaryData);

                const instancesData = (res.metrics.labeledOrgMetrics[TCIMetricsLabelType.tci_apps_instances] || [])
                    .filter(data => filter(data));

                const totalExecutionsData = (res.metrics.labeledOrgMetrics[TCIMetricsLabelType.tci_apps_total_executions] || [])
                    .filter(data => filter(data));

                this.genTenantSummaryData(instancesData, totalExecutionsData, summaryData);

                const topExecutionsData = (res.metrics.labeledOrgMetrics[TCIMetricsLabelType.tci_apps_top_executions] || [])
                    .filter(data => filter(data));
                this.genTopExecData(topExecutionsData);
            });
    }

    getEnvVarsService(): NgEnvVarsService {
        return this.envVarsService;
    }

    /**
     * get websocket ng service
     */
    getWsService(): ITCIWebSocketService {
        return this.websocketService;
    }

    getSummaryDataSubject() {
        return this.summaryData$;
    }

    getStatusFilterSubject() {
        return this.statusFilter$;
    }

    setStatusFilterSubject(status: IMetricsStatusSummaryData) {
        this.statusFilter$.next(status);
    }

    getDashboardRefSub() {
        return this.dashboardRefreshSub$;
    }

    setDashboardRefSub(ref: IMetricsRefresh) {
        this.dashboardRefreshSub$.next(ref);
    }

    getTenantSummaryDataSubject() {
        return this.totalExecutionsData$;
    }

    getTopExecutionsDataSubject() {
        return this.topExecutionsData$;
    }

    private genStateSummaryData(summaryData: ITCIMetricsStatus[]): void {
        const data: IMetricsStatusSummary = {
            goodNum: {
                name: "Good",
                type: TCIAppStatusCategory.READY,
                percentage: 0,
                value: 0
            },
            errorNum: {
                name: "Error",
                type: TCIAppStatusCategory.ERROR,
                percentage: 0,
                value: 0
            },
            warningNum: {
                name: "Warning",
                type: TCIAppStatusCategory.WARNING,
                percentage: 0,
                value: 0
            },
            notRunningNum: {
                name: "Not running",
                type: TCIAppStatusCategory.NOT_READY,
                percentage: 0,
                value: 0
            },
            totalApps: {
                name: "Total",
                type: null,
                percentage: 100,
                value: 0
            }
        };

        summaryData
            .forEach(r => {
                switch (r.labels.status as TCIMetricsAppStatusType) {
                    case TCIMetricsAppStatusType.error:
                        data.errorNum.value += lodash.toNumber(r.value);
                        break;
                    case TCIMetricsAppStatusType.good:
                        data.goodNum.value += lodash.toNumber(r.value);
                        break;
                    case TCIMetricsAppStatusType.stopped:
                        data.notRunningNum.value += lodash.toNumber(r.value);
                        break;
                    case TCIMetricsAppStatusType.warning:
                        data.warningNum.value += lodash.toNumber(r.value);
                        break;
                }
                data.totalApps.value += lodash.toNumber(r.value);
            });

        lodash.forEach(data, d => d.percentage = Math.round((d.value / data.totalApps.value) * 100));

        this.summaryData$.next(data);
    }

    // tenant-summary
    private genTenantSummaryData(
        instancesData: ITCIMetricsStatus[],
        totalExecutionsData: ITCIMetricsStatus[],
        summaryData: ITCIMetricsStatus[]
    ): void {
        const data: { [key: string]: IMetricsExecutionsSummary } = {};
        const initData = (tenantType: TCITenantAppType) => {
            if (!data[tenantType.toString()]) { // Initialize object
                data[tenantType.toString()] = {
                    tenantType,
                    appCount: 0,
                    instancesCount: 0,
                    successCount: 0,
                    failedCount: 0
                };
            }
        };

        instancesData.forEach(insData => {
            const tenantType = TCITenantAppType.fromString(insData.labels.app_type);
            initData(tenantType);
            data[tenantType.toString()].instancesCount = lodash.toNumber(insData.value);
        });

        totalExecutionsData.forEach(totExecData => {
            const tenantType = TCITenantAppType.fromString(totExecData.labels.app_type);
            initData(tenantType);
            switch (totExecData.labels.status as TCIMetricsExecutionsStatusType) {
                case TCIMetricsExecutionsStatusType.success:
                    data[tenantType.toString()].successCount = lodash.toNumber(totExecData.value);
                    break;
                case TCIMetricsExecutionsStatusType.failure:
                    data[tenantType.toString()].failedCount = lodash.toNumber(totExecData.value);
                    break;
            }
        });

        summaryData.forEach(d => {
            const record = data[TCITenantAppType.fromString(d.labels.app_type).toString()];
            if (record) {
                record.appCount += Number(d.value);
            }
        });

        this.totalExecutionsData$.next(lodash.toArray(data));
    }

    // top-exec
    private genTopExecData(topExecutionsData: ITCIMetricsStatus[]): void {
        const data: { [key: string]: IMetricsTopExecutions } = {};
        topExecutionsData
            .filter(r => Number(r.value) > 0)
            .forEach(r => { // Return aggregated data per application
                const app = this.apps.find(a => a.id === r.labels.app_id && a.type.equals(TCITenantAppType.fromString(r.labels.app_type)));
                if (app) {
                    if (!data[r.labels.app_id]) {
                        data[r.labels.app_id] = { app, count: Number(r.value) };
                    } else {
                        data[r.labels.app_id].count += Number(r.value);
                    }
                }
            });

        this.topExecutionsData$.next(lodash.orderBy(data, ["count", "app.name"], ["desc", "asc"]));
    }

    getMetrics(): Observable<ITCIMetrics> {
        return this.metricsHttpObs$;
    }

    /**
     * rzavala 1 apr 2020
     * The following methods are for testing WS express API I/O
     */

    // getAppMetrics(appId: string, appType: TCITenantAppType): Observable<ITCIMetrics> {
    //     return this.http.get(`${SERVICE_URLS.METRICS_RECORDS}/${appId}?${QUERY_PARAMS.APP_TYPE}=${appType.toString()}`)
    //         .map(response => response.json());
    // }

    // registerApps(appIds: string[]): Observable<IMetricsRegistration[]> {
    //     return this.http.put(`${SERVICE_URLS.METRICS_REGISTER}?${QUERY_PARAMS.METRICS_REGISTER_APPS}=${appIds.join(",")}`, {})
    //         .map(response => response.json());
    // }

    // unRegisterApps(appIds: string[]): Observable<IMetricsRegistration[]> {
    //     return this.http.delete(`${SERVICE_URLS.METRICS_REGISTER}?${QUERY_PARAMS.METRICS_REGISTER_APPS}=${appIds.join[","]}`, {})
    //         .map(response => response.json());
    // }
}
